import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
	TaskType,
	HardwareScanTestResult,
	HardwareScanProtocolModule,
	WatcherStepProcess,
} from 'src/app/modules/hardware-scan/enums/hardware-scan.enum';
import { ModalPreScanInfoComponent } from '../components/modal/modal-pre-scan-info/modal-pre-scan-info.component';
import { HardwareScanResultService } from './hardware-scan-result.service';
import { HardwareScanService } from './hardware-scan.service';
import { RecoverBadSectorsService } from './recover-bad-sectors.service';
import { PreviousResultService } from './previous-result.service';
import { ModalCancelComponent } from '../components/modal/modal-cancel/modal-cancel.component';
import { MatDialog } from '@lenovo/material/dialog';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { Module } from '../models/hardware-scan.interface';

declare const Windows;

@Injectable({
	providedIn: 'root',
})
export class ScanExecutionService {
	private startScanClicked = false;
	private devicesRecoverBadSectors: any[];
	private rbsStartDate: Date;
	private progress = 0;
	private culture: any;
	private modulesStored: any;
	private lastModules: HardwareScanProtocolModule;
	private cancelWatcher;

	private cancelHandler = {
		cancel: undefined,
	};

	constructor(
		private hardwareScanService: HardwareScanService,
		private hardwareScanResultService: HardwareScanResultService,
		private recoverBadSectorsService: RecoverBadSectorsService,
		private dialog: MatDialog,
		private translate: TranslateService,
		private previousResultService: PreviousResultService,
		private logger: LoggerService
	) {
		this.culture = this.hardwareScanService.getCulture();

		/** Used to manage a watcher from scan process.
		 * If front-end receive a response after checkCliRunning timer is over, * the timer resets and the workflow starts again.
		 * If front-end doesn't receive a response from checkCliRunning, it will stay at the view results page with cancelled status
		 */
		this.hardwareScanService.watcherProcess.subscribe((status) => {
			switch (status) {
				case WatcherStepProcess.Start:
					const startIntervalTime = 10000;

					this.checkCliRunning(startIntervalTime);
					break;
				case WatcherStepProcess.Intermediate:
					const intermediateIntervalTime = 4000;

					clearInterval(this.cancelWatcher);
					this.checkCliRunning(intermediateIntervalTime, async () => {
						await this.hardwareScanService.onScanResponseError();
						this.hardwareScanService.doScanRequestReset();
						this.doScanExecutionReset();
					});
					break;
				case WatcherStepProcess.Stop:
					clearInterval(this.cancelWatcher);
					break;
				default:
					clearInterval(this.cancelWatcher);
					break;
			}
		});
	}

	// Getters and Setters
	public set deviceInRecover(value: string) {
		this.hardwareScanService.setDeviceInRecover(value);
	}
	public get deviceInRecover(): string {
		return this.hardwareScanService.getDeviceInRecover();
	}

	public set modules(value: any) {
		this.modulesStored = value;
	}

	public get modules(): any {
		return this.modulesStored;
	}

	public setLastModules(value: HardwareScanProtocolModule) {
		this.lastModules = value;
	}

	public getLastModules(): any {
		return this.lastModules;
	}

	public set setDevicesRecoverBadSectors(devices: any) {
		this.devicesRecoverBadSectors = devices;
	}

	public set executionProgress(value: any) {
		this.progress = value;
	}

	public get executionProgress(): any {
		return this.progress;
	}

	public get scanClicked(): boolean {
		return this.startScanClicked;
	}

	public set scanClicked(value: boolean) {
		this.startScanClicked = value;
	}

	public get devicesToRecoverBadSectors(): any[] {
		return this.devicesRecoverBadSectors;
	}

	public set devicesToRecoverBadSectors(devices: any[]) {
		this.devicesRecoverBadSectors = devices;
	}

	// Recover Bad Sectors Functions
	public doRecoverBadSectors() {
		this.progress = 0;

		const devicesId = [];

		this.devicesRecoverBadSectors = this.hardwareScanService.getDevicesRecover();
		this.standardizeRbsResponse();
		if (this.devicesRecoverBadSectors[0].name) {
			this.deviceInRecover = this.devicesRecoverBadSectors[0].name;
		}

		for (const storageDevice of this.devicesRecoverBadSectors) {
			devicesId.push(storageDevice.id);
		}

		const payload = {
			devices: devicesId,
		};

		if (this.hardwareScanService) {
			this.rbsStartDate = new Date();
			this.hardwareScanService
				.getRecoverBadSectors(payload)
				.then((response) => {
					this.hardwareScanService.setEnableViewResults(true);
				})
				.catch((error) =>
					this.logger.exception('[ScanExecutionService] doRecoverBadSectors', error)
				)
				.finally(() => {
					this.onViewResultsRecover();
				});
		}
	}

	/* This function is used to standardize the RBS intermediate response with the same used by a Scan. */
	public standardizeRbsResponse() {
		const moduleInformation = this.hardwareScanService.getModulesRetrieved();
		if (moduleInformation) {
			const storageModule = moduleInformation.categoryList.find(
				(category) => category.id === 'storage'
			);

			const results = { items: [] };

			for (const device of this.devicesRecoverBadSectors) {
				const item = {
					module: storageModule.name,
					icon: storageModule.id,
					name: device.name,
					expanded: false,
					detailsExpanded: false,
					listTest: [
						{
							id: '',
							name: device.name,
							statusTest: device.status,
							percent: device.percent,
						},
					],
				};

				results.items.push(item);
			}
			this.modules = results.items;
		}
	}

	public onViewResultsRecover() {
		const moduleInformation = this.hardwareScanService.getModulesRetrieved();
		if (moduleInformation) {
			const storageModule = moduleInformation.categoryList.find(
				(category) => category.id === 'storage'
			);

			const results = {
				resultModule: HardwareScanTestResult.Pass,
				startDate: this.rbsStartDate,
				date: new Date(),
				items: [],
			};

			for (const device of this.devicesRecoverBadSectors) {
				const item = {
					module: storageModule.name,
					icon: storageModule.id,
					name: device.name,
					deviceId: device.id,
					expanded: false,
					detailsExpanded: false,
					details: [
						{ key: 'numberSectors', value: device.numberOfSectors },
						{ key: 'numberBadSectors', value: device.numberOfBadSectors },
						{ key: 'numberFixedSectors', value: device.numberOfFixedSectors },
						{ key: 'numberNonFixedSectors', value: device.numberOfNonFixedSectors },
						{ key: 'elapsedTime', value: device.elapsedTime },
					],
					listTest: [
						{
							id: device.id,
							name: device.name,
							statusTest: device.status,
							percent: device.percent,
						},
					],
				};

				results.items.push(item);
			}

			results.resultModule = this.hardwareScanResultService.consolidateResults(
				this.devicesRecoverBadSectors.map((item) => item.status)
			);

			this.recoverBadSectorsService.setRecoverResultItems(results);
			this.modules = results.items;
		}
	}

	public generateRequestAndResponseScan(
		taskType: TaskType,
		scanAgain = false,
		moduleExecution = HardwareScanProtocolModule.all
	) {
		let requests;
		let response;

		this.hardwareScanService.cleanResponses();

		if (taskType === TaskType.QuickScan && moduleExecution === HardwareScanProtocolModule.all) {
			response = this.hardwareScanService.getQuickScanResponse();
			requests = this.hardwareScanService.getQuickScanRequest();
		} else {
			response = this.hardwareScanService
				.getQuickScanResponse()
				.filter((moduleName) => moduleName.id === moduleExecution);
			requests = this.hardwareScanService
				.getQuickScanRequest()
				.filter((moduleName) => moduleName.moduleId === moduleExecution);
		}

		if (taskType === TaskType.CustomScan) {
			if (scanAgain) {
				response = this.hardwareScanService.getLastFilteredCustomScanResponse();
				requests = this.hardwareScanService.getLastFilteredCustomScanRequest();
			} else {
				response = this.hardwareScanService.getFilteredCustomScanResponse();
				requests = this.hardwareScanService.getFilteredCustomScanRequest();
			}
			// Saving the selected filter in case the user wants to redo the same scan
			this.hardwareScanService.setLastFilteredCustomScanResponse(response);
			this.hardwareScanService.setLastFilteredCustomScanRequest(requests);
		}

		this.setLastModules(moduleExecution);
		return [response, requests];
	}

	// Scan Execution Functions
	public checkPreScanInfo(
		taskType: TaskType,
		scanAgain = false,
		moduleExecution = HardwareScanProtocolModule.all
	) {
		let requests;
		[this.modules, requests] = this.generateRequestAndResponseScan(
			taskType,
			scanAgain,
			moduleExecution
		);

		// Resets the 'expanded' state and User visibility to the default value (closed)
		this.modules.forEach((module) => {
			module.currentModule = false;
			module.expanded = false;
			module.expandedStatusChangedByUser = false;
		});

		this.validateBatteryModal(requests);
	}

	public onViewResults() {
		const results = {
			finalResultCode: this.hardwareScanService.getFinalResultCode(),
			date: this.hardwareScanService.getFinalResultStartDate(),
			information: this.hardwareScanService.getFinalResultDescription(),
			items: [],
		};

		for (const module of this.modules) {
			let moduleId = module.id;
			if (!this.hardwareScanService.getIsDesktopMachine()) {
				if (moduleId === 'pci_express') {
					moduleId += '_laptop';
				}
			}

			const item = {
				id: module.id,
				module: module.module,
				name: module.name,
				resultCode: module.resultCode,
				information: module.description,
				expanded: false,
				expandedStatusChangedByUser: false,
				detailsExpanded: false,
				icon: moduleId,
				details: this.previousResultService.buildDetails(module),
				listTest: [],
				resultModule: HardwareScanTestResult.Pass,
			};

			for (const test of module.listTest) {
				item.listTest.push({
					id: test.id,
					name: test.name,
					statusTest: test.statusTest,
					information: test.description,
				});
			}

			item.resultModule = this.hardwareScanResultService.consolidateResults(
				item.listTest.map((itemTest) => itemTest.statusTest)
			);
			results.items.push(item);
		}

		// Update the ViewResultItem
		this.previousResultService.setViewResultItems(results);

		// If a cancellation was requested, the application will return the HW Scan home page.
		// So, in this case, the modules list will be updated with the data to be displayed on
		// the home screen (this.getItemToDisplay()) the modules name and description
		// If the scan finished without cancellation, then the scan result will be display.
		// In this case, the module list is updated with the scan results and modules details (results.items)
		if (!this.hardwareScanService.isCancelRequestByUser()) {
			this.modules = results.items;
		} else {
			this.modules = this.getItemToDisplay();
		}
	}

	getItemToDisplay() {
		// Shows a default component list
		if (!this.hardwareScanService.isShowComponentList()) {
			return this.hardwareScanService.getInitialHardwareComponentList();
		}

		// Shows the real component list, retrieved after a Quick/Custom scan
		const devices = [];
		const modules = this.hardwareScanService.getModulesRetrieved();
		if (modules !== undefined) {
			for (const categoryInfo of modules.categoryList) {
				for (const groupList of categoryInfo.groupList) {
					const group = groupList;
					const info = categoryInfo.name;
					let icon = categoryInfo.id;
					if (!this.hardwareScanService.getIsDesktopMachine()) {
						if (categoryInfo.id === 'pci_express') {
							icon += '_laptop';
						}
					}
					devices.push({
						module: info,
						name: group.name,
						icon,
					});
				}
			}
		}
		return devices;
	}

	public onCancelScan() {
		let isCancelingRBS;
		if (this.hardwareScanService) {
			isCancelingRBS = this.hardwareScanService.isRecoverExecuting();
		}

		const modalCancel = this.dialog.open(ModalCancelComponent, {
			maxWidth: '50rem',
			autoFocus: true,
			hasBackdrop: true,
			disableClose: true,
			panelClass: 'cancel-modal-hwscan',
		});

		// If the scan/rbs process has finished and the cancelation modal is still opened,
		// alert the user that the process cannot be canceled anymore.
		const scanFinished = this.hardwareScanService.isWorkDone().subscribe((done) => {
			modalCancel.componentInstance.showProcessFinishedMessage();
		});

		modalCancel.componentInstance.cancelRequested.subscribe(() => {
			// If the cancelation process has started, unsubscribe from the other subject to avoid any odd behavior
			scanFinished.unsubscribe();

			if (this.hardwareScanService) {
				if (!isCancelingRBS) {
					clearInterval(this.cancelWatcher);

					// Let's start monitoring the CLI during the cancelation process.
					// If it closes and no response is received (through isWorkDone() subject), the front-end will
					// be redirected to the HardwareScan home page (forcing an init through a refresh modules call), preventing
					// that the application gets stuck.
					const cancelWaitIntervalTime = 3000;

					this.checkCliRunning(cancelWaitIntervalTime, () => {
						this.hardwareScanService.setIsScanDone(true);
						this.hardwareScanService.setScanExecutionStatus(false);
						this.hardwareScanService.setScanOrRBSFinished(true);
						this.cleaningUpScan(undefined);
						this.refreshModules();

						modalCancel.close();
						if (this.cancelHandler && this.cancelHandler.cancel) {
							this.cancelHandler.cancel();
						}
					});
				}
				this.hardwareScanService.cancelScanExecution();

				this.hardwareScanService.isWorkDone().subscribe((done) => {
					if (!isCancelingRBS) {
						clearInterval(this.cancelWatcher);
					}
					if (done) {
						// When the cancelation is done, close the cancelation dialog.
						// Sets the status of the scan to avoid problems when viewing their results
						// (without that, the back button doesn't work as expected!) only if we're not canceling a RBS!
						modalCancel.close();
						if (!isCancelingRBS) {
							this.hardwareScanService.setIsScanDone(false);
						}
					}
				});
			}
		});
	}

	public checkCliRunning(time, callBack = undefined) {
		const cancelWatcherDelay = time;

		// Workaround for RTC changing date/time problem!
		// NOTICE: Remove this code piece as soon as this problem is fixed
		this.cancelWatcher = setInterval(() => {
			this.hardwareScanService.getStatus().then((result: any) => {
				if (!result.isScanInProgress) {
					clearInterval(this.cancelWatcher);

					if (callBack) {
						callBack();
					}
				}
			});
		}, cancelWatcherDelay);
	}

	private doScanExecutionReset() {
		this.cleaningUpScan(undefined);

		// Defines information about module details
		this.onViewResults();
		this.modules.forEach((module) => {
			module.expanded = true;
		});
	}

	public refreshModules() {
		this.hardwareScanService.setLoadingStatus(false);
		this.hardwareScanService.reloadItemsToScan(true);
		this.hardwareScanService.initLoadingModules(this.culture);

		this.hardwareScanService.isHardwareModulesLoaded().subscribe((loaded) => {
			if (loaded) {
				this.modules = this.getItemToDisplay();
			}
		});
	}

	private isDevicePluggedIn() {
		// If powerSupplyStatus === notPresent, machine is not plugged.
		// Otherwise this device is plugged

		return (
			Windows?.System.Power?.PowerManager.powerSupplyStatus !==
			Windows?.System.Power?.PowerSupplyStatus.notPresent
		);
	}

	private validateBatteryModal(requests: any) {
		const minimalBatteryLevelAllowed = 20;

		if (
			Windows?.System.Power?.PowerManager.remainingChargePercent <
				minimalBatteryLevelAllowed &&
			!this.isDevicePluggedIn()
		) {
			const modal = this.dialog.open(ModalPreScanInfoComponent, {
				maxWidth: '50rem',
				autoFocus: true,
				hasBackdrop: true,
				disableClose: true,
				panelClass: 'hardware-scan-modal-size',
			});

			// This CONNECT_POWER string is a token that originally came from the HardwareScan plugin and it's used
			// to indicated that the System is running on low battery and, therefore should be plugged in before starting a new scan.
			modal.componentInstance.description = 'CONNECT_POWER';
			modal.componentInstance.error = this.translate.instant('hardwareScan.warning');

			modal.afterClosed().subscribe(
				() => {
					this.validateAudioControllerModal(requests);
				},
				() => {
					this.hardwareScanService.cleanCustomTests();
					// User has clicked in the 'X' button, so we also need to re-enable the Quick/Custom scan button here.
					this.startScanClicked = false;
				}
			);
		} else {
			this.validateAudioControllerModal(requests);
		}
	}

	/*
	 * Used to start a scan, 0 is a quick scan, and 1 is a custom scan
	 */
	private getDoScan(requests: any): any {
		this.progress = 0;

		const payload = {
			requests,
			categories: [],
			localizedItems: [],
		};

		if (this.hardwareScanService) {
			this.hardwareScanService.setFinalResponse(null);
			this.hardwareScanService
				.getDoScan(payload, this.modules, this.cancelHandler)
				.then((response) => {
					this.cleaningUpScan(response);
				})
				.catch((ex: any) => {
					this.logger.exception(`[ScanExecutionService] getDoScan`, ex);
				})
				.finally(() => {
					this.doScanExecutionReset();
				});
		}

		return false;
	}

	private validateAudioControllerModal(requests: Array<Module>) {
		// Finds if there is an Output Stream Test selected on the requests list.
		const isOutputStreamTestSelected =
			requests.find((module) =>
				module.testRequestList.find((test) => test.id.includes('TEST_OUTPUT_STREAM_TEST'))
			) !== undefined;

		if (isOutputStreamTestSelected) {
			const modal = this.dialog.open(ModalPreScanInfoComponent, {
				maxWidth: '50rem',
				autoFocus: true,
				hasBackdrop: true,
				disableClose: true,
				panelClass: 'hardware-scan-modal-size',
			});

			modal.componentInstance.description = this.translate.instant(
				'hardwareScan.pluginTokens.NOTE_SOUND_SET_PERCENTAGE'
			);
			modal.componentInstance.error = this.translate.instant('hardwareScan.warning');

			modal.afterClosed().subscribe(() => {
				this.getDoScan(requests);
				// User has clicked in the OK button, so we need to re-enable the Quick/Custom scan button here
				this.startScanClicked = false;
			});
		} else {
			this.getDoScan(requests);
		}
	}

	private cleaningUpScan(response: any): boolean {
		if (response) {
			this.hardwareScanService.setFinalResponse(response);
		}

		this.startScanClicked = false;
		if (!this.hardwareScanService.isCancelRequestByUser()) {
			this.hardwareScanService.setEnableViewResults(true);
		} else {
			return true;
		}
		return false;
	}

	public isRefreshingModules() {
		return this.hardwareScanService.isRefreshingModules();
	}
}
